// Switching databases is a deliberate step on Data Management, not a dropdown
// in the header (36.12.21): a switch changes the data, the rules, the
// corrections, the declared prefixes and the questions every page reads, so
// it asks first and says so. Shown only when the site holds more than one
// database; the header names the database in use and offers no switch.

import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { buttonVariants } from '@/components/ui/Button';
import { messageClass } from '@/components/ui/Message';
import { cn } from '@/lib/utils';
import { effectiveDatabase, useActiveDatabase, useDatabases } from '@/lib/useDatabases';

export function DatabaseSwitch() {
  const databases = useDatabases();
  const { active, switchTo } = useActiveDatabase();
  const [picked, setPicked] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const list = databases.data;
  if (!list || list.databases.length <= 1) return null;

  const currentId = effectiveDatabase(list, active) ?? list.default;
  const current = list.databases.find((d) => d.id === currentId);
  const chosenId = picked ?? currentId;
  const chosen = list.databases.find((d) => d.id === chosenId);
  const changed = chosenId !== currentId;

  return (
    <div className="mt-3 flex flex-wrap items-end gap-2" data-testid="database-switch">
      <label className="flex flex-col gap-1 text-sm">
        <span>Switch to</span>
        <select
          aria-label="Switch to database"
          value={chosenId}
          onChange={(e) => setPicked(e.target.value)}
          className="rounded-md border border-border bg-card px-2 py-1 text-sm"
        >
          {list.databases.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
              {d.default ? ' (default)' : ''}
              {d.id === currentId ? ' — in use' : ''}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        className={buttonVariants({ variant: 'secondary', size: 'sm' })}
        disabled={!changed}
        onClick={() => setConfirming(true)}
      >
        Switch
      </button>
      {confirming && chosen && current && (
        <Modal
          title={`Switch every page to ${chosen.name}?`}
          titleId="database-switch-title"
          onClose={() => setConfirming(false)}
          size="md"
          footer={
            <>
              <button
                type="button"
                className={buttonVariants({ variant: 'secondary', size: 'sm' })}
                onClick={() => setConfirming(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={buttonVariants({ variant: 'primary', size: 'sm' })}
                onClick={() => {
                  switchTo(chosen.id === list.default ? null : chosen.id);
                  setPicked(null);
                  setConfirming(false);
                }}
              >
                Switch to {chosen.name}
              </button>
            </>
          }
        >
          <p className={cn(messageClass('guidance', 'normal'), 'text-sm')}>
            You are reading <span className="font-medium">{current.name}</span>. Switching changes
            what every page shows and where every load, clear and question goes: the data, the
            rules, the corrections, the declared prefixes and the recent questions are those of{' '}
            <span className="font-medium">{chosen.name}</span>, and each page's remembered graph
            choice is forgotten.
          </p>
        </Modal>
      )}
    </div>
  );
}
